import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Check, ChevronsUpDown, User, MapPin, Package, FileText, AlertCircle, Camera, QrCode, Edit3, CheckCircle } from 'lucide-react';
import { QRScannerDialog, SignatureDialog } from './recibiform/index';
import { useQRScanner } from '../hooks/useQRScanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ELEMENTOS_OPTIONS = [
  { id: 'Monitor+Dock_Station', label: 'Monitor + Dock Station', icon: '🖥️' },
  { id: 'Ordenador_portatil', label: 'Ordenador Portátil', icon: '💻' },
  { id: 'Smartphone', label: 'Smartphone', icon: '📱' },
  { id: 'Tablet', label: 'Tablet', icon: '📲' },
  { id: 'Otros', label: 'Otros', icon: '📦' }
];

export default function RecibiForm() {
  const [formData, setFormData] = useState({
    employee_id: null,
    nombre_completo: '',
    email: '',
    lugar_trabajo: '',
    es_otro: false,
    es_correcta_ubicacion: null,
    elementos: [],
    codigo_monitor: '',
    codigo_tablet: '',
    codigo_smartphone: '',
    codigo_portatil: '',
    otros_codigos: '',
    etiqueta_portatil: '',
    etiqueta_monitor: '',
    etiqueta_tablet: '',
    etiqueta_smartphone: '',
    otras_etiquetas: '',
    codigo_econocom: '',
    pack: '',
    perifericos: '',
    firma_base64: '',
    observaciones: ''
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [employeeResults, setEmployeeResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openCombobox, setOpenCombobox] = useState(false);

  // QR Scanner: estado encapsulado en custom hook + ciclo de vida en QRScannerDialog
  const {
    isScanning: scannerOpen,
    scanningFor,
    startScanning: openQRScanner,
    stopScanning: closeScanner,
  } = useQRScanner();

  // Signature state
  const [signatureOpen, setSignatureOpen] = useState(false);
  const signatureRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // BACKEND_URL is a constant, encodeURIComponent is native - safe to omit
  const searchEmployees = useCallback(async (query) => {
    setLoading(true);
    try {
      const data = await apiGet('/api/employees/search', { q: query });
      setEmployeeResults(data.results || []);
    } catch (error) {
      toast.error('Error al buscar empleados');
    } finally {
      setLoading(false);
    }
  }, []);

  // Search employees
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery && searchQuery.length >= 2 && !formData.es_otro) {
        searchEmployees(searchQuery);
      } else {
        setEmployeeResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, formData.es_otro, searchEmployees, setEmployeeResults]);

  const handleSelectEmployee = (employee) => {
    setFormData({
      ...formData,
      employee_id: employee.id,
      nombre_completo: employee.nombre_completo,
      email: employee.email,
      lugar_trabajo: employee.lugar_trabajo,
      es_otro: false
    });
    setSearchQuery(employee.nombre_completo);
    setOpenCombobox(false);
  };

  const handleSelectOtro = () => {
    setFormData({
      ...formData,
      employee_id: null,
      nombre_completo: '',
      email: '',
      lugar_trabajo: '',
      es_otro: true,
      es_correcta_ubicacion: null
    });
    setSearchQuery('');
    setEmployeeResults([]);
    setOpenCombobox(false);
  };

  const handleElementoToggle = (elementoId) => {
    const elementos = formData.elementos.includes(elementoId)
      ? formData.elementos.filter(e => e !== elementoId)
      : [...formData.elementos, elementoId];
    
    setFormData({ ...formData, elementos });
  };

  // QR Scanner callback: al recibir un código del dialog, lo setea en el campo correcto
  const handleQRScanSuccess = useCallback((decodedText) => {
    if (!scanningFor) return;
    setFormData(prev => ({ ...prev, [scanningFor]: decodedText }));
  }, [scanningFor]);

  // Signature functions
  const openSignaturePad = () => {
    setSignatureOpen(true);
  };

  const saveSignature = () => {
    if (signatureRef.current) {
      const dataURL = signatureRef.current.toDataURL();
      setFormData({ ...formData, firma_base64: dataURL });
      setSignatureOpen(false);
      toast.success('Firma guardada correctamente');
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (!formData.nombre_completo.trim()) {
      toast.error('Por favor, selecciona un empleado o elige "OTRO"');
      return;
    }

    if (!formData.lugar_trabajo.trim()) {
      toast.error('Por favor, indica el lugar de trabajo');
      return;
    }

    if (formData.elementos.length === 0) {
      toast.error('Por favor, selecciona al menos un elemento recibido');
      return;
    }

    if (!formData.firma_base64) {
      toast.error('Por favor, firma el documento antes de enviar');
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/receipts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (data.success) {
        toast.success('Recibí registrado correctamente', {
          description: `Material entregado a ${formData.nombre_completo}`
        });

        // Reset form
        setFormData({
          employee_id: null,
          nombre_completo: '',
          email: '',
          lugar_trabajo: '',
          es_otro: false,
          es_correcta_ubicacion: null,
          elementos: [],
          codigo_monitor: '',
          codigo_tablet: '',
          codigo_smartphone: '',
          codigo_portatil: '',
          otros_codigos: '',
          etiqueta_portatil: '',
          etiqueta_monitor: '',
          etiqueta_tablet: '',
          etiqueta_smartphone: '',
          otras_etiquetas: '',
          codigo_econocom: '',
          pack: '',
          perifericos: '',
          firma_base64: '',
          observaciones: ''
        });
        setSearchQuery('');
        if (signatureRef.current) {
          signatureRef.current.clear();
        }
      } else {
        toast.error('Error al registrar el recibí');
      }
    } catch (error) {
      toast.error('Error al enviar el formulario', { description: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '0', maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#1a5c2e', marginBottom: '8px' }}>
          📦 Formulario de Recibí de Dispositivos IT
        </h1>
        <p style={{ fontSize: '15px', color: '#666' }}>
          Registro de entrega de equipamiento tecnológico con escaneo QR/Código de barras
        </p>
      </div>

      {/* Info Card */}
      <Card style={{ marginBottom: '24px', background: '#f0f8f2', borderColor: '#cfe3d6' }}>
        <CardContent style={{ padding: '20px' }}>
          <p style={{ fontSize: '14px', color: '#1a5c2e', marginBottom: '8px', fontWeight: 600 }}>
            ℹ️ Instrucciones
          </p>
          <ul style={{ fontSize: '13px', color: '#555', lineHeight: 1.8, paddingLeft: '20px', margin: 0 }}>
            <li>Busca al empleado por nombre o selecciona <strong>"OTRO"</strong> para nuevos ingresos</li>
            <li>Marca los elementos IT recibidos y escanea sus códigos QR/Barcode</li>
            <li>Completa las etiquetas de inventario para cada dispositivo</li>
            <li><strong>Firma digital obligatoria</strong> al final del formulario</li>
          </ul>
        </CardContent>
      </Card>

      {/* Main Form */}
      <form onSubmit={handleSubmit}>
        {/* Employee Selection Card */}
        <Card data-testid="recibi-form-employee-card" style={{ marginBottom: '20px' }}>
          <CardHeader>
            <CardTitle style={{ fontSize: '18px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User style={{ width: '20px', height: '20px' }} />
              Datos del Empleado
            </CardTitle>
            <CardDescription>Busca al empleado o selecciona OTRO</CardDescription>
          </CardHeader>
          <Separator />
          <CardContent style={{ padding: '24px' }}>
            {/* Employee Search */}
            <div style={{ marginBottom: '24px' }}>
              <Label htmlFor="employee-search" style={{ marginBottom: '8px', display: 'block' }}>
                Buscar Empleado *
              </Label>
              
              <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="recibi-employee-search-button"
                    variant="outline"
                    role="combobox"
                    type="button"
                    aria-expanded={openCombobox}
                    style={{
                      width: '100%',
                      justifyContent: 'space-between',
                      fontWeight: formData.es_otro || formData.nombre_completo ? 600 : 400,
                      color: formData.es_otro ? '#d97706' : (formData.nombre_completo ? '#1a5c2e' : '#888')
                    }}
                  >
                    {formData.es_otro ? '🆕 OTRO (Nuevo ingreso)' : (formData.nombre_completo || 'Selecciona un empleado...')}
                    <ChevronsUpDown style={{ marginLeft: '8px', width: '16px', height: '16px', opacity: 0.5 }} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent style={{ width: '100%', maxWidth: '700px', padding: '0' }}>
                  <Command>
                    <CommandInput
                      data-testid="recibi-employee-search-input"
                      placeholder="Escribe un nombre para buscar..."
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {loading ? 'Buscando...' : searchQuery.length < 2 ? 'Escribe al menos 2 caracteres' : 'No se encontraron empleados'}
                      </CommandEmpty>
                      
                      <CommandGroup heading="Nuevo ingreso">
                        <CommandItem
                          data-testid="recibi-select-otro"
                          onSelect={handleSelectOtro}
                          style={{ cursor: 'pointer', padding: '12px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                            <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#fff3cd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                              🆕
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: '#d97706' }}>OTRO</div>
                              <div style={{ fontSize: '12px', color: '#888' }}>Nuevo empleado sin registro previo</div>
                            </div>
                            {formData.es_otro && <Check style={{ width: '16px', height: '16px', color: '#1a5c2e' }} />}
                          </div>
                        </CommandItem>
                      </CommandGroup>

                      {employeeResults.length > 0 && (
                        <CommandGroup heading="Empleados encontrados">
                          {employeeResults.map((emp) => (
                            <CommandItem
                              key={emp.id}
                              data-testid={`recibi-employee-option-${emp.id}`}
                              onSelect={() => handleSelectEmployee(emp)}
                              style={{ cursor: 'pointer', padding: '12px' }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#f0f8f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                                  👤
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600, color: '#1a1a1a' }}>{emp.nombre_completo}</div>
                                  <div style={{ fontSize: '12px', color: '#888' }}>{emp.lugar_trabajo}</div>
                                </div>
                                {formData.employee_id === emp.id && <Check style={{ width: '16px', height: '16px', color: '#1a5c2e' }} />}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* If OTRO selected */}
            {formData.es_otro && (
              <div style={{ marginBottom: '24px', padding: '16px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <AlertCircle style={{ width: '18px', height: '18px', color: '#d97706' }} />
                  <span style={{ fontWeight: 600, color: '#d97706' }}>Nuevo empleado sin registro</span>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <Label htmlFor="nombre-completo">Nombre completo *</Label>
                    <Input
                      id="nombre-completo"
                      data-testid="recibi-nombre-completo-input"
                      placeholder="Nombre y apellidos"
                      value={formData.nombre_completo}
                      onChange={(e) => setFormData({ ...formData, nombre_completo: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email-otro">Email</Label>
                    <Input
                      id="email-otro"
                      type="email"
                      placeholder="email@valorizasm.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '16px' }}>
                  <Label htmlFor="lugar-trabajo-otro">Lugar de Trabajo *</Label>
                  <Input
                    id="lugar-trabajo-otro"
                    data-testid="recibi-lugar-trabajo-input"
                    placeholder="Ej: Oficina Madrid, Planta Sevilla..."
                    value={formData.lugar_trabajo}
                    onChange={(e) => setFormData({ ...formData, lugar_trabajo: e.target.value })}
                    required
                  />
                </div>
              </div>
            )}

            {/* If employee selected (not OTRO) */}
            {!formData.es_otro && formData.nombre_completo && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: '#f0f8f2', border: '1px solid #cfe3d6', borderRadius: '8px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <User style={{ width: '18px', height: '18px', color: '#1a5c2e' }} />
                    <span style={{ fontWeight: 600, color: '#1a5c2e' }}>{formData.nombre_completo}</span>
                  </div>
                  {formData.email && (
                    <div style={{ fontSize: '13px', color: '#555', marginBottom: '6px' }}>
                      📧 {formData.email}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#555' }}>
                    <MapPin style={{ width: '14px', height: '14px' }} />
                    <span>{formData.lugar_trabajo}</span>
                  </div>
                </div>

                {/* Location confirmation - ONLY if NOT OTRO */}
                <div>
                  <Label style={{ marginBottom: '12px', display: 'block' }}>
                    ¿Es correcta la ubicación registrada? *
                  </Label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Button
                      type="button"
                      data-testid="recibi-ubicacion-si"
                      variant={formData.es_correcta_ubicacion === true ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, es_correcta_ubicacion: true })}
                      style={{
                        flex: 1,
                        background: formData.es_correcta_ubicacion === true ? '#2d8a4e' : 'transparent',
                        color: formData.es_correcta_ubicacion === true ? '#fff' : '#555'
                      }}
                    >
                      ✓ Sí, es correcta
                    </Button>
                    <Button
                      type="button"
                      data-testid="recibi-ubicacion-no"
                      variant={formData.es_correcta_ubicacion === false ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, es_correcta_ubicacion: false })}
                      style={{
                        flex: 1,
                        background: formData.es_correcta_ubicacion === false ? '#dc2626' : 'transparent',
                        color: formData.es_correcta_ubicacion === false ? '#fff' : '#555'
                      }}
                    >
                      ✗ No, es incorrecta
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Elements Selection Card */}
        <Card style={{ marginBottom: '20px' }} data-testid="recibi-elementos-card">
          <CardHeader>
            <CardTitle style={{ fontSize: '18px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Package style={{ width: '20px', height: '20px' }} />
              Elementos IT Recibidos
            </CardTitle>
            <CardDescription>Marca los dispositivos entregados al empleado</CardDescription>
          </CardHeader>
          <Separator />
          <CardContent style={{ padding: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {ELEMENTOS_OPTIONS.map((elemento) => (
                <div
                  key={elemento.id}
                  style={{
                    padding: '16px',
                    border: formData.elementos.includes(elemento.id) ? '2px solid #2d8a4e' : '2px solid #e5e7eb',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: formData.elementos.includes(elemento.id) ? '#f0f8f2' : '#fff',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <Checkbox
                    checked={formData.elementos.includes(elemento.id)}
                    onCheckedChange={() => handleElementoToggle(elemento.id)}
                    data-testid={`recibi-elemento-${elemento.id}`}
                  />
                  <div style={{ fontSize: '24px' }}>{elemento.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#1a1a1a', fontSize: '14px' }}>{elemento.label}</div>
                  </div>
                  {formData.elementos.includes(elemento.id) && (
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#2d8a4e' }} />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Conditional Device Details Cards */}
        {formData.elementos.length > 0 && (
          <Card style={{ marginBottom: '20px' }} data-testid="recibi-device-details-card">
            <CardHeader>
              <CardTitle style={{ fontSize: '18px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <QrCode style={{ width: '20px', height: '20px' }} />
                Códigos y Etiquetas de Dispositivos
              </CardTitle>
              <CardDescription>Escanea los códigos QR/Barcode y registra las etiquetas de inventario</CardDescription>
            </CardHeader>
            <Separator />
            <CardContent style={{ padding: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Monitor */}
                {formData.elementos.includes('Monitor+Dock_Station') && (
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <h4 style={{ fontWeight: 600, marginBottom: '12px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🖥️ Monitor + Dock Station
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <Label>Código QR/Barcode *</Label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Input
                            value={formData.codigo_monitor}
                            onChange={(e) => setFormData({ ...formData, codigo_monitor: e.target.value })}
                            placeholder="Código del monitor"
                            data-testid="recibi-codigo-monitor-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openQRScanner('codigo_monitor')}
                            data-testid="recibi-scan-monitor-button"
                          >
                            <Camera style={{ width: '16px', height: '16px' }} />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Etiqueta Inventario *</Label>
                        <Input
                          value={formData.etiqueta_monitor}
                          onChange={(e) => setFormData({ ...formData, etiqueta_monitor: e.target.value })}
                          placeholder="Etiqueta a pegar"
                          data-testid="recibi-etiqueta-monitor-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Portátil */}
                {formData.elementos.includes('Ordenador_portatil') && (
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <h4 style={{ fontWeight: 600, marginBottom: '12px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      💻 Ordenador Portátil
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <Label>Código QR/Barcode *</Label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Input
                            value={formData.codigo_portatil}
                            onChange={(e) => setFormData({ ...formData, codigo_portatil: e.target.value })}
                            placeholder="Código del portátil"
                            data-testid="recibi-codigo-portatil-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openQRScanner('codigo_portatil')}
                            data-testid="recibi-scan-portatil-button"
                          >
                            <Camera style={{ width: '16px', height: '16px' }} />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Etiqueta Inventario *</Label>
                        <Input
                          value={formData.etiqueta_portatil}
                          onChange={(e) => setFormData({ ...formData, etiqueta_portatil: e.target.value })}
                          placeholder="Etiqueta a pegar"
                          data-testid="recibi-etiqueta-portatil-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Smartphone */}
                {formData.elementos.includes('Smartphone') && (
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <h4 style={{ fontWeight: 600, marginBottom: '12px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      📱 Smartphone
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <Label>Código QR/Barcode *</Label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Input
                            value={formData.codigo_smartphone}
                            onChange={(e) => setFormData({ ...formData, codigo_smartphone: e.target.value })}
                            placeholder="Código del smartphone"
                            data-testid="recibi-codigo-smartphone-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openQRScanner('codigo_smartphone')}
                            data-testid="recibi-scan-smartphone-button"
                          >
                            <Camera style={{ width: '16px', height: '16px' }} />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Etiqueta Inventario *</Label>
                        <Input
                          value={formData.etiqueta_smartphone}
                          onChange={(e) => setFormData({ ...formData, etiqueta_smartphone: e.target.value })}
                          placeholder="Etiqueta a pegar"
                          data-testid="recibi-etiqueta-smartphone-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Tablet */}
                {formData.elementos.includes('Tablet') && (
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <h4 style={{ fontWeight: 600, marginBottom: '12px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      📲 Tablet
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <Label>Código QR/Barcode *</Label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Input
                            value={formData.codigo_tablet}
                            onChange={(e) => setFormData({ ...formData, codigo_tablet: e.target.value })}
                            placeholder="Código de la tablet"
                            data-testid="recibi-codigo-tablet-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openQRScanner('codigo_tablet')}
                            data-testid="recibi-scan-tablet-button"
                          >
                            <Camera style={{ width: '16px', height: '16px' }} />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Etiqueta Inventario *</Label>
                        <Input
                          value={formData.etiqueta_tablet}
                          onChange={(e) => setFormData({ ...formData, etiqueta_tablet: e.target.value })}
                          placeholder="Etiqueta a pegar"
                          data-testid="recibi-etiqueta-tablet-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Otros */}
                {formData.elementos.includes('Otros') && (
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <h4 style={{ fontWeight: 600, marginBottom: '12px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      📦 Otros Elementos
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <Label>Código QR/Barcode</Label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Input
                            value={formData.otros_codigos}
                            onChange={(e) => setFormData({ ...formData, otros_codigos: e.target.value })}
                            placeholder="Código del elemento"
                            data-testid="recibi-otros-codigos-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openQRScanner('otros_codigos')}
                            data-testid="recibi-scan-otros-button"
                          >
                            <Camera style={{ width: '16px', height: '16px' }} />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Etiqueta Inventario</Label>
                        <Input
                          value={formData.otras_etiquetas}
                          onChange={(e) => setFormData({ ...formData, otras_etiquetas: e.target.value })}
                          placeholder="Etiqueta a pegar"
                          data-testid="recibi-otras-etiquetas-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Info Card */}
        <Card style={{ marginBottom: '20px' }}>
          <CardHeader>
            <CardTitle style={{ fontSize: '18px', color: '#1a5c2e' }}>
              Información Adicional
            </CardTitle>
            <CardDescription>Código Econocom, pack asignado y periféricos</CardDescription>
          </CardHeader>
          <Separator />
          <CardContent style={{ padding: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <Label htmlFor="codigo-econocom">Código Econocom</Label>
                <Input
                  id="codigo-econocom"
                  value={formData.codigo_econocom}
                  onChange={(e) => setFormData({ ...formData, codigo_econocom: e.target.value })}
                  placeholder="Código interno"
                  data-testid="recibi-codigo-econocom-input"
                />
              </div>
              <div>
                <Label htmlFor="pack">Pack Asignado</Label>
                <Input
                  id="pack"
                  value={formData.pack}
                  onChange={(e) => setFormData({ ...formData, pack: e.target.value })}
                  placeholder="Pack según registros"
                  data-testid="recibi-pack-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="perifericos">Periféricos</Label>
              <Textarea
                id="perifericos"
                value={formData.perifericos}
                onChange={(e) => setFormData({ ...formData, perifericos: e.target.value })}
                placeholder="Teclado, ratón, auriculares, etc..."
                rows={2}
                data-testid="recibi-perifericos-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Observations Card */}
        <Card style={{ marginBottom: '20px' }}>
          <CardHeader>
            <CardTitle style={{ fontSize: '18px', color: '#1a5c2e' }}>
              Observaciones
            </CardTitle>
            <CardDescription>Añade cualquier nota adicional</CardDescription>
          </CardHeader>
          <Separator />
          <CardContent style={{ padding: '24px' }}>
            <Textarea
              value={formData.observaciones}
              onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
              placeholder="Comentarios, preguntas, incidencias..."
              rows={3}
              data-testid="recibi-observaciones-input"
            />
          </CardContent>
        </Card>

        {/* Signature Card */}
        <Card style={{ marginBottom: '20px', borderColor: formData.firma_base64 ? '#2d8a4e' : '#e5e7eb' }}>
          <CardHeader>
            <CardTitle style={{ fontSize: '18px', color: '#1a5c2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Edit3 style={{ width: '20px', height: '20px' }} />
              Firma Digital *
            </CardTitle>
            <CardDescription>Confirma que has recibido todos los equipos correctamente</CardDescription>
          </CardHeader>
          <Separator />
          <CardContent style={{ padding: '24px' }}>
            {formData.firma_base64 ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ padding: '16px', background: '#f0f8f2', borderRadius: '8px', marginBottom: '12px' }}>
                  <img src={formData.firma_base64} alt="Firma" style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px' }} />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({ ...formData, firma_base64: '' })}
                  data-testid="recibi-clear-signature-button"
                >
                  Borrar y Firmar de Nuevo
                </Button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '32px', background: '#f9fafb', borderRadius: '8px', border: '2px dashed #d1d5db' }}>
                <Edit3 style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
                <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                  Pulsa el botón para abrir el panel de firma
                </p>
                <Button
                  type="button"
                  onClick={openSignaturePad}
                  data-testid="recibi-open-signature-button"
                  style={{ background: '#2d8a4e', color: '#fff' }}
                >
                  <Edit3 style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Firmar Documento
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (window.confirm('¿Seguro que quieres limpiar el formulario?')) {
                setFormData({
                  employee_id: null,
                  nombre_completo: '',
                  email: '',
                  lugar_trabajo: '',
                  es_otro: false,
                  es_correcta_ubicacion: null,
                  elementos: [],
                  codigo_monitor: '',
                  codigo_tablet: '',
                  codigo_smartphone: '',
                  codigo_portatil: '',
                  otros_codigos: '',
                  etiqueta_portatil: '',
                  etiqueta_monitor: '',
                  etiqueta_tablet: '',
                  etiqueta_smartphone: '',
                  otras_etiquetas: '',
                  codigo_econocom: '',
                  pack: '',
                  perifericos: '',
                  firma_base64: '',
                  observaciones: ''
                });
                setSearchQuery('');
              }
            }}
            disabled={submitting}
          >
            Limpiar
          </Button>
          <Button
            type="submit"
            data-testid="recibi-submit-button"
            disabled={submitting || !formData.firma_base64}
            style={{
              background: submitting || !formData.firma_base64 ? '#ccc' : '#2d8a4e',
              color: '#fff',
              minWidth: '200px'
            }}
          >
            {submitting ? (
              <>⏳ Registrando...</>
            ) : (
              <>
                <FileText style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                Registrar Recibí
              </>
            )}
          </Button>
        </div>
      </form>

      {/* QR Scanner Dialog - encapsulado en componente dedicado */}
      <QRScannerDialog
        open={scannerOpen}
        onOpenChange={(open) => {
          if (!open) closeScanner();
        }}
        onScanSuccess={handleQRScanSuccess}
      />

      {/* Signature Pad Dialog */}
      <SignatureDialog
        open={signatureOpen}
        onOpenChange={setSignatureOpen}
        signatureRef={signatureRef}
        onClear={clearSignature}
        onSave={saveSignature}
      />
    </div>
  );
}

